/**
 * The RunMyTrivia product brand.
 *
 * Two brands exist (see docs/branding.md):
 *   - The product brand, below. The operator domain (runmytrivia.com)
 *     shows it on every signed-out screen: sign-in, sign-up, the workspace
 *     picker, and the pre-auth split screen.
 *   - Each org's own brand (siteConfig). The public venue sites, the
 *     operator app after sign-in, and the projector show it.
 * The operator domain never reads the Host-header org (DEFAULT_PUBLIC_ORG_ID)
 * for branding. A venue site never shows the product brand, except the
 * optional "Powered by" mark on the projector.
 *
 * Change the product name, logo, or color here only.
 */

export const PRODUCT_BRAND = {
  name: 'RunMyTrivia',
  // Placeholder. Empty means "show the name as a text wordmark".
  logoUrl: '',
  // The same pink the operator app shows now. nearestRadixAccent maps it
  // to crimson.
  accentColor: '#840032',
  siteUrl: 'https://runmytrivia.com',
  // The company that owns the product and bills for it. Stripe shows this
  // name on checkout and on card statements. The West Slope Quiz venue
  // brand (westslopequiz.com) is separate and keeps its own name.
  companyLegalName: 'West Slope Quiz Co. LLC',
  // The card statement descriptor set in the Stripe dashboard (22 chars max).
  statementDescriptor: 'WEST SLOPE QUIZ CO LLC',
} as const;

// "RunMyTrivia is a product of West Slope Quiz Co. LLC"
export const PRODUCT_COMPANY_LINE = `${PRODUCT_BRAND.name} is a product of ${PRODUCT_BRAND.companyLegalName}`;

// Hostnames that serve the operator product instead of a venue site.
// ponytail: hostname list, not per-tenant config. Change it when a second
// operator domain exists.
export const OPERATOR_HOSTS = ['runmytrivia.com', 'www.runmytrivia.com'];

export function isOperatorHost(hostname: string): boolean {
  return OPERATOR_HOSTS.includes(hostname);
}

export const IS_OPERATOR_HOST =
  typeof window !== 'undefined' && isOperatorHost(window.location.hostname);

// The product brand in siteConfig shape, so the signed-out screens can
// keep calling useSiteConfig().get(...) on the operator domain.
export const PRODUCT_SITE_CONFIG: Record<string, string> = {
  brandName: PRODUCT_BRAND.name,
  brandLogoUrl: PRODUCT_BRAND.logoUrl,
  brandColor: PRODUCT_BRAND.accentColor,
  publicSiteUrl: PRODUCT_BRAND.siteUrl,
  socialInstagramUrl: '',
  socialInstagramHandle: '',
};

// index.html carries the West Slope Quiz title for the venue site. On the
// operator domain, replace the title and og:site_name with the product.
export function applyProductDocumentMeta(): void {
  if (!IS_OPERATOR_HOST || typeof document === 'undefined') return;
  document.title = PRODUCT_BRAND.name;
  document
    .querySelector('meta[property="og:site_name"]')
    ?.setAttribute('content', PRODUCT_BRAND.name);
}
